import { useState, useEffect } from 'react';
import { blogService, collegeService } from '../lib/database';
import { getBlogPostSeoSettings, getCollegeSeoSettings } from '../lib/seoManager';

interface SEOData {
  title: string;
  description: string;
  keywords: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'college';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

export function useSEOData(contentType: 'blogs' | 'colleges', slug?: string) {
  const [seoData, setSeoData] = useState<SEOData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchSEOData() {
      if (!slug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        if (contentType === 'blogs') {
          // Fetch blog post data
          const post = await blogService.getBySlug(slug);
          if (post) {
            setSeoData({
              title: post.meta_title || post.title,
              description: post.meta_description || post.excerpt,
              keywords: post.meta_keywords || (post.tags ? post.tags.join(', ') : ''),
              image: post.og_image || post.image_url,
              url: `/blog/${post.slug}`,
              type: 'article',
              author: post.author,
              publishedTime: post.created_at,
              modifiedTime: post.updated_at
            });
          }
        } else if (contentType === 'colleges') {
          // Fetch college data
          const college = await collegeService.getBySlug(slug);
          if (college) {
            setSeoData({
              title: college.meta_title || `${college.name} - College Details & Reviews | Grad Vikalp`,
              description: college.meta_description || `Complete guide to ${college.name} - ${college.type} ${college.category} college in ${college.location}, ${college.state}. Get fees, cutoffs, reviews & admission details.`,
              keywords: college.meta_keywords || `${college.name}, ${college.category} college, ${college.location}, ${college.state}, college reviews, admission, fees, cutoffs`,
              image: college.og_image || college.image_url,
              url: `/college/${college.slug}`,
              type: 'college'
            });
          }
        }
      } catch (err) {
        console.error('Error fetching SEO data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch SEO data');
      } finally {
        setLoading(false);
      }
    }

    fetchSEOData();
  }, [contentType, slug]);

  return { seoData, loading, error };
}